import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Target, Plus, Trophy, Pencil, Trash2, Loader2,
  Phone, Mail, Calendar, TrendingUp, Award, DollarSign,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  { value: "calls",         label: "Calls",          icon: Phone,       suffix: "" },
  { value: "emails",        label: "Emails",         icon: Mail,        suffix: "" },
  { value: "meetings",      label: "Meetings",       icon: Calendar,    suffix: "" },
  { value: "opportunities", label: "Opportunities",  icon: TrendingUp,  suffix: "" },
  { value: "closed_deals",  label: "Closed Deals",   icon: Award,       suffix: "" },
  { value: "revenue",       label: "Revenue",        icon: DollarSign,  suffix: "$" },
];

const PERIODS = [
  { value: "daily",     label: "Daily"     },
  { value: "weekly",    label: "Weekly"    },
  { value: "monthly",   label: "Monthly"   },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual",    label: "Annual"    },
];

const MANAGER_ROLES = ["owner", "admin", "sales_manager"];

function metricMeta(metric) {
  return METRICS.find((m) => m.value === metric) || METRICS[0];
}

function formatValue(metric, n) {
  const meta = metricMeta(metric);
  if (meta.value === "revenue") {
    return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return Number(n || 0).toLocaleString();
}

export default function SalesGoalsPage({ user }) {
  const [currentUser, setCurrentUser] = useState(user || null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null); // a goal object when editing
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    user_id: "", metric: "calls", period: "weekly", target: 50, notes: "",
  });

  // Leaderboard
  const [lbMetric, setLbMetric] = useState("calls");
  const [lbPeriod, setLbPeriod] = useState("weekly");
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading] = useState(false);

  const effectiveUser = user || currentUser;
  const isManager = MANAGER_ROLES.includes(effectiveUser?.role) || effectiveUser?.system_role === "platform_owner";
  const myUserId = effectiveUser?.user_id;

  useEffect(() => {
    fetchAll();
    if (!user) fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isManager) fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lbMetric, lbPeriod, isManager]);

  const fetchMe = async () => {
    const r = await fetch(`${API}/auth/me`, { credentials: "include" });
    if (r.ok) setCurrentUser(await r.json());
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [gr, ur] = await Promise.all([
        fetch(`${API}/sales-goals?active_only=false`, { credentials: "include" }),
        fetch(`${API}/users`, { credentials: "include" }),
      ]);
      if (gr.ok) setGoals((await gr.json()).goals || []);
      if (ur.ok) setTeamMembers(await ur.json());
    } catch {
      toast.error("Failed to load goals");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    setLbLoading(true);
    try {
      const r = await fetch(`${API}/sales-goals/leaderboard?metric=${lbMetric}&period=${lbPeriod}`, {
        credentials: "include",
      });
      if (r.ok) setLeaderboard((await r.json()).leaderboard || []);
    } finally {
      setLbLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ user_id: myUserId || "", metric: "calls", period: "weekly", target: 50, notes: "" });
    setCreateOpen(true);
  };

  const openEdit = (goal) => {
    setEditing(goal);
    setForm({
      user_id: goal.user_id,
      metric: goal.metric,
      period: goal.period,
      target: goal.target,
      notes: goal.notes || "",
    });
    setCreateOpen(true);
  };

  const handleSave = async () => {
    if (!form.user_id) { toast.error("Pick a user"); return; }
    if (!form.target || form.target <= 0) { toast.error("Target must be > 0"); return; }

    setSaving(true);
    try {
      let res;
      if (editing) {
        res = await fetch(`${API}/sales-goals/${editing.goal_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ target: Number(form.target), notes: form.notes }),
        });
      } else {
        res = await fetch(`${API}/sales-goals`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            user_id: form.user_id,
            metric: form.metric,
            period: form.period,
            target: Number(form.target),
            notes: form.notes || null,
          }),
        });
      }
      const data = await res.json();
      if (res.ok) {
        toast.success(editing ? "Goal updated" : "Goal created");
        setCreateOpen(false);
        fetchAll();
        if (isManager) fetchLeaderboard();
      } else {
        toast.error(data.detail || "Save failed");
      }
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (goal) => {
    if (!window.confirm(`Delete ${goal.metric}/${goal.period} goal for ${goal.user_name}?`)) return;
    const r = await fetch(`${API}/sales-goals/${goal.goal_id}`, {
      method: "DELETE", credentials: "include",
    });
    if (r.ok) { toast.success("Goal deleted"); fetchAll(); if (isManager) fetchLeaderboard(); }
    else { toast.error("Delete failed"); }
  };

  const myGoals = useMemo(() => goals.filter((g) => g.user_id === myUserId), [goals, myUserId]);
  const teamGoals = useMemo(() => goals.filter((g) => g.user_id !== myUserId), [goals, myUserId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="sales-goals-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Target className="w-7 h-7 text-emerald-600" />
            Sales Goals
          </h1>
          <p className="text-slate-500 mt-1">
            Track targets across calls, emails, meetings, opportunities and revenue.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="new-goal-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Goal
        </Button>
      </div>

      <Tabs defaultValue="mine" className="space-y-4">
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-my-goals">My Goals</TabsTrigger>
          {isManager && <TabsTrigger value="team" data-testid="tab-team-goals">Team Goals</TabsTrigger>}
          {isManager && <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">Leaderboard</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine">
          <GoalGrid goals={myGoals} onEdit={openEdit} onDelete={handleDelete} emptyHint="Set yourself a target by clicking 'New Goal'." />
        </TabsContent>

        {isManager && (
          <TabsContent value="team">
            <GoalGrid goals={teamGoals} onEdit={openEdit} onDelete={handleDelete} showUser emptyHint="No team goals yet. Click 'New Goal' and pick a team member." />
          </TabsContent>
        )}

        {isManager && (
          <TabsContent value="leaderboard">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Team Leaderboard
                </CardTitle>
                <CardDescription>
                  Live progress across the org for the chosen metric &amp; period.
                </CardDescription>
                <div className="flex gap-3 pt-2">
                  <Select value={lbMetric} onValueChange={setLbMetric}>
                    <SelectTrigger className="w-[180px]" data-testid="lb-metric"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={lbPeriod} onValueChange={setLbPeriod}>
                    <SelectTrigger className="w-[160px]" data-testid="lb-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {lbLoading ? (
                  <div className="text-center py-6 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
                ) : leaderboard.length === 0 ? (
                  <p className="text-center py-6 text-slate-500">No team members yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rank</TableHead>
                        <TableHead>Rep</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leaderboard.map((row, i) => (
                        <TableRow key={row.user_id} data-testid={`lb-row-${row.user_id}`}>
                          <TableCell className="font-medium">{i + 1}</TableCell>
                          <TableCell>
                            <div>{row.user_name}</div>
                            <Badge variant="outline" className="text-xs">{row.role?.replace("_", " ")}</Badge>
                          </TableCell>
                          <TableCell className="font-semibold text-emerald-700">{formatValue(lbMetric, row.progress)}</TableCell>
                          <TableCell>{row.has_goal ? formatValue(lbMetric, row.target) : <span className="text-slate-400">—</span>}</TableCell>
                          <TableCell>
                            {row.has_goal ? (
                              <div className="flex items-center gap-2 w-32">
                                <Progress value={row.percent_complete} className="h-2" />
                                <span className="text-xs text-slate-500 w-12 text-right">{row.percent_complete}%</span>
                              </div>
                            ) : <span className="text-slate-400">no goal</span>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Goal" : "New Goal"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update the target or notes." : "Pick the rep, metric, period and target."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {!editing && (
              <div className="space-y-2">
                <Label>Who is this for?</Label>
                {isManager ? (
                  <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                    <SelectTrigger data-testid="goal-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((m) => (
                        <SelectItem key={m.user_id} value={m.user_id}>{m.name} {m.user_id === myUserId ? "(you)" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={effectiveUser?.name || ""} disabled />
                )}
              </div>
            )}

            {!editing && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Metric</Label>
                  <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
                    <SelectTrigger data-testid="goal-metric-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {METRICS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Period</Label>
                  <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
                    <SelectTrigger data-testid="goal-period-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Target {metricMeta(form.metric).value === "revenue" ? "($)" : ""}</Label>
              <Input
                type="number" min={1}
                value={form.target}
                onChange={(e) => setForm({ ...form, target: e.target.value })}
                data-testid="goal-target-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything to remember about this goal..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="goal-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Target className="w-4 h-4 mr-2" />}
              Save Goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function GoalGrid({ goals, onEdit, onDelete, showUser = false, emptyHint }) {
  if (!goals || goals.length === 0) {
    return <Card><CardContent className="py-12 text-center text-slate-500">{emptyHint || "No goals yet."}</CardContent></Card>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {goals.map((g) => {
        const meta = metricMeta(g.metric);
        const Icon = meta.icon;
        const done = g.percent_complete >= 100;
        return (
          <Card key={g.goal_id} data-testid={`goal-card-${g.goal_id}`} className={done ? "border-emerald-300 bg-emerald-50/30" : ""}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className="w-4 h-4 text-emerald-600" />
                    {meta.label}
                  </CardTitle>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{g.period}</Badge>
                    {showUser && <Badge variant="secondary" className="text-xs">{g.user_name}</Badge>}
                    {g.is_self_set && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Self-set</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(g)} data-testid={`edit-goal-${g.goal_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onDelete(g)} className="text-red-600" data-testid={`delete-goal-${g.goal_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-emerald-700">{formatValue(g.metric, g.current_progress)}</span>
                <span className="text-sm text-slate-500">/ {formatValue(g.metric, g.target)}</span>
              </div>
              <div className="space-y-1">
                <Progress value={g.percent_complete} className="h-2" />
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>{g.percent_complete}% complete</span>
                  <span>{formatValue(g.metric, g.remaining)} to go</span>
                </div>
              </div>
              {g.notes && <p className="text-xs text-slate-500 pt-1 border-t">{g.notes}</p>}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
